'use client';

import React from 'react';
import classNames from 'classnames';
import { ConTextAtom } from '@gandalf/babylon/atoms/text';
import { ConIconAtom, type SvgIcon } from '@gandalf/babylon/atoms/icon';
import cls from './InfoContent.module.scss';
import { Image } from '@frodo/ui/tokens/image/Image';

export type InfoContentProps = {
  icon?: SvgIcon;
  subHeader: string;
  paragraphText: string;
  rightContent?: React.ReactNode;
  pinRightContent?: boolean; /** When true, rightContent stays fixed on the right (no wrap); text shrinks/wraps. Default false. */
  imageSrc?: string;
  imageAlt?: string;
  className?: string;
  qa?: string;
};

export const InfoContent = ({
  imageSrc,
  icon,
  subHeader,
  paragraphText,
  rightContent,
  pinRightContent = false,
  className,
  imageAlt,
  qa,
}: InfoContentProps) => {
  const showIcon = Boolean(icon) && !imageSrc;

  return (
    <div
      className={classNames(cls.container, className, {
        [cls.contentWithImage]: !!imageSrc,
        [cls.contentWithIcon]: showIcon,
      })}
      data-qa={qa ?? 'info-content'}
    >
      {imageSrc && (
        <div className={cls.imageWrapper}>
          <Image
            src={imageSrc}
            alt={imageAlt ?? ''}
            className={cls.image}
            ignoreAspectRatio
            width={56}
            height={56}
            qa="info-content-image"
          />
        </div>
      )}
      {showIcon && icon && (
        <div className={cls.icon}>
          <ConIconAtom
            icon={icon}
            size="16"
            color="fill-neutral-3"
            cls={cls.icon}
          />
        </div>
      )}
      <div
        className={classNames(cls.content, {
          [cls.pinRightContent]: pinRightContent,
        })}
      >
        <div className={cls.text}>
          {subHeader && (
            <ConTextAtom
              tag="p"
              fontSizes="small"
              fontWeight="bold"
              cls={cls.subHeader}
            >
              {subHeader}
            </ConTextAtom>
          )}
          {paragraphText && (
            <ConTextAtom tag="p" fontSizes="small" cls={cls.paragraph}>
              {paragraphText}
            </ConTextAtom>
          )}
        </div>
        {rightContent && <div className={cls.rightContent}>{rightContent}</div>}
      </div>
    </div>
  );
};
